import { TextFieldProps } from "@mui/material";
import { DatePicker as MuiDatePicker, DatePickerProps, DatePickerSlotProps } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import React, { useCallback, useMemo } from "react";

type IMuiDatePickerProps = Omit<DatePickerProps<dayjs.Dayjs>, "value" | "onChange" | "renderInput">;
type IDatePickerTextFieldProps = Pick<TextFieldProps, "fullWidth" | "size" | "label" | "helperText">;

export interface IDatePickerProps extends IMuiDatePickerProps {
    error?: boolean;
    value: Date | undefined;
    textFieldProps?: IDatePickerTextFieldProps;
    slotFieldProps?: DatePickerSlotProps<dayjs.Dayjs, false>["field"];

    onChange?(value: Date | undefined): void;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export const DatePicker: React.FC<IDatePickerProps> = ({
    error,
    value,
    textFieldProps,
    slotFieldProps,
    onChange,
    onBlur,
    ...muiDatePickerProps
}) => {
    const inputValue = useMemo((): dayjs.Dayjs | null => (value == null ? null : dayjs(value)), [value]);

    const handleChange = useCallback(
        // We have to convert to a string and back to a date to ensure that the timezone is correct,
        // otherwise we get off-by-one errors when selecting non-DST times during DST periods.
        //
        // For example if we select 05/02/2000, it works fine:
        //  date                {..., $d: Tue May 02 2000 00:00:00 GMT+0200 (Central European Summer Time), ...}
        //  date.toDate()                `Tue May 02 2000 00:00:00 GMT+0200 (Central European Summer Time)`
        //
        // But if we select 02/02/2000, it breaks:
        //  date                {..., $d: Wed Feb 02 2000 00:00:00 GMT+0100 (Central European Standard Time), ...}
        //  date.toDate()                `Tue Feb 01 2000 23:00:00 GMT+0100 (Central European Standard Time)`
        //
        // The dayjs utc plugin adds a hidden prop to the toDate() function that applies the timezone offset
        // correctly, but it doesn't expose it in the types definition. Instead we can just rip that line out:
        // https://github.com/iamkun/dayjs/blame/a9d7d0398d22ebd4bfc3812ca0134a97606d54d9/src/plugin/utc/index.js#L133-L139
        (date: dayjs.Dayjs | null): void =>
            onChange?.(date?.isValid() ? dayjs(date.format("YYYY-MM-DD")).toDate() : undefined),
        [onChange],
    );

    return (
        <MuiDatePicker<dayjs.Dayjs>
            {...muiDatePickerProps}
            value={inputValue}
            onChange={handleChange}
            slotProps={{ textField: { size: "small", ...textFieldProps, error, onBlur }, field: slotFieldProps }}
        />
    );
};
